// recova GmbH – Öffentliche Startseite: Hero, USPs, Ablauf, CTAs.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, eur } from "@/lib/api";
import { Recycle, PhoneCall, ShieldCheck, CreditCard, Wrench, Leaf, ArrowRight, CalendarCheck, MapPin, PackageCheck } from "lucide-react";

const USPS = [
  { icon: <Leaf className="h-6 w-6" />, title: "Kreislaufwirtschaft & recova-DNA", text: "Als Recycling-Spezialisten nutzen wir unser Know-how, um Geräte nachhaltig zu erhalten, statt sie vorschnell zu verschrotten." },
  { icon: <ShieldCheck className="h-6 w-6" />, title: "Transparente Anfahrts- & Diagnosepauschale", text: "Faire Fehleranalyse vor Ort. Entscheiden Sie sich für die Reparatur, wird die Pauschale vollständig verrechnet." },
  { icon: <Wrench className="h-6 w-6" />, title: "Erfahrene festangestellte Techniker", text: "Qualifizierte Fachtechniker mit bestens ausgestatteten Service-Fahrzeugen – keine Subunternehmer." },
  { icon: <CreditCard className="h-6 w-6" />, title: "Bezahlung erst nach Erledigung", text: "Bequeme Zahlung vor Ort per EC-/Kreditkarte (Tap-to-Pay), PayPal oder Überweisung." },
  { icon: <Recycle className="h-6 w-6" />, title: "recova Altgeräte-Garantie", text: "Bei wirtschaftlichem Totalschaden übernehmen wir die fachgerechte Entsorgung." },
];

const STEPS = [
  { icon: <CalendarCheck className="h-5 w-5" />, title: "Online anfragen", text: "Gerät, Fehler & Wunschtermin in 2 Minuten erfassen – inkl. Typenschild-Guide." },
  { icon: <PhoneCall className="h-5 w-5" />, title: "Terminbestätigung", text: "Unsere Disposition bestätigt Ihren Termin per E-Mail – mit Live-Tracking-Link." },
  { icon: <MapPin className="h-5 w-5" />, title: "Techniker vor Ort", text: "Festangestellte Fachtechniker mit Ersatzteilen im Service-Fahrzeug." },
  { icon: <PackageCheck className="h-5 w-5" />, title: "Repariert & bezahlt", text: "Erst zahlen, wenn Ihr Gerät wieder läuft – per Karte, PayPal oder Rechnung." },
];

export default function LandingPage() {
  const [diagnosisFee, setDiagnosisFee] = useState(null);
  useEffect(() => {
    API.get("/meta/appliances").then((r) => setDiagnosisFee(r.data.diagnosis_fee)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-[#f8faf9]">
      <header className="glass-header" data-testid="landing-header">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="recova Logo" className="h-10 w-10 rounded-full object-cover" data-testid="logo" />
            <div>
              <p className="font-heading text-xl font-extrabold text-[#1b4332]">recova</p>
              <p className="text-[10px] uppercase tracking-widest text-[#028090]">Hausgeräte-Service</p>
            </div>
          </div>
          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex">
            <a href="#vorteile" className="transition-colors hover:text-[#1b4332]" data-testid="nav-vorteile">Vorteile</a>
            <a href="#ablauf" className="transition-colors hover:text-[#1b4332]" data-testid="nav-ablauf">Ablauf</a>
          </nav>
          <Link to="/buchen" data-testid="header-cta-book">
            <button className="btn-primary text-sm" data-testid="header-booking-button">Termin anfragen</button>
          </Link>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-4 sm:px-6" data-testid="hero-section">
        <div className="grid grid-cols-1 items-center gap-8 py-12 md:py-20 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <span className="badge-eco" data-testid="hero-badge">Nachhaltig. Lokal. Fair.</span>
            <h1 className="font-heading mt-5 text-4xl font-extrabold tracking-tight text-[#1b4332] sm:text-5xl lg:text-6xl" data-testid="hero-headline">
              Reparieren statt Wegwerfen – Ihr nachhaltiger Hausgeräte-Service von recova.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-[#475569] md:text-lg" data-testid="hero-subline">
              Schnell, transparent und fair. Wir bringen Ihre Waschmaschine, Spülmaschine &amp; Co. wieder zum Laufen – direkt bei Ihnen vor Ort.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/buchen" data-testid="hero-cta-book">
                <button className="btn-primary px-7 py-3.5 text-base" data-testid="hero-booking-button">
                  Reparatur-Termin online anfragen <ArrowRight className="h-5 w-5" />
                </button>
              </Link>
              <a href="tel:022715059265" className="btn-secondary px-6 py-3.5 text-base" data-testid="hero-phone-button">
                <PhoneCall className="h-5 w-5" /> 02271 - 5059265
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-slate-500">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#2a9d8f]" /> Festangestellte Techniker</span>
              <span className="flex items-center gap-2"><CreditCard className="h-4 w-4 text-[#2a9d8f]" /> Zahlung erst nach Erledigung</span>
              <span className="flex items-center gap-2"><Leaf className="h-4 w-4 text-[#2a9d8f]" /> Altgeräte-Garantie</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-3 rounded-3xl bg-[#2ec4b6]/15" />
              <img
                src="https://images.unsplash.com/photo-1604335399105-a0c585fd81a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHxf333YXNoaW5nJTIwbWFjaGluZXxlbnwwfHx8fDE3ODg4ODM0MDZ8MA&ixlib=rb-4.1.0&q=85"
                alt="Moderne Waschmaschine – recova repariert Hausgeräte nachhaltig"
                className="relative aspect-[4/3] w-full rounded-3xl border border-slate-200 object-cover shadow-2xl"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-5 -left-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl">
                <p className="font-mono2 text-xs uppercase tracking-widest text-[#028090]">Diagnosepauschale</p>
                <p className="font-heading text-2xl font-extrabold text-[#1b4332]" data-testid="hero-diagnosis-fee">{diagnosisFee != null ? eur(diagnosisFee) : "…"} <span className="text-sm font-medium text-slate-500">– wird verrechnet</span></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="vorteile" className="border-y border-slate-200 bg-white py-16" data-testid="usp-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="font-mono2 text-xs uppercase tracking-widest text-[#028090]">Warum recova</p>
          <h2 className="font-heading mt-2 text-2xl font-bold tracking-tight text-[#0f172a] sm:text-3xl lg:text-4xl">Fünf gute Gründe für die Reparatur</h2>
          <div className="my-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            {USPS.map((u, i) => (
              <div key={i} className={`card-hover rounded-2xl border border-slate-200 bg-[#f8faf9] p-6 ${i === 0 ? "md:col-span-2" : ""}`} data-testid={`usp-card-${i}`}>
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[#1b4332] text-white">{u.icon}</div>
                <h3 className="font-heading text-lg font-semibold text-[#0f172a]">{u.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#475569]">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="ablauf" className="py-16" data-testid="steps-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="font-mono2 text-xs uppercase tracking-widest text-[#028090]">So funktioniert's</p>
          <h2 className="font-heading mt-2 text-2xl font-bold tracking-tight text-[#0f172a] sm:text-3xl">In vier Schritten zum laufenden Gerät</h2>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={i} className="relative rounded-2xl border border-slate-200 bg-white p-6" data-testid={`step-card-${i}`}>
                <span className="font-heading absolute right-4 top-4 text-4xl font-extrabold text-[#e8f0ec]">{i + 1}</span>
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-[#028090] text-white">{s.icon}</div>
                <h4 className="font-heading text-lg font-medium text-[#1b4332]">{s.title}</h4>
                <p className="mt-1.5 text-sm text-slate-500">{s.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 rounded-3xl bg-[#1b4332] p-8 text-center sm:p-12" data-testid="cta-banner">
            <h3 className="font-heading text-2xl font-bold text-white sm:text-3xl">Gerät defekt? Wir kümmern uns.</h3>
            <p className="mx-auto mt-3 max-w-lg text-sm text-emerald-100/80">Online anfragen, Live-Status verfolgen und erst nach erfolgreicher Reparatur zahlen.</p>
            <Link to="/buchen" data-testid="banner-cta-book">
              <button className="mt-6 inline-flex items-center gap-2 rounded-lg bg-[#2ec4b6] px-7 py-3.5 font-semibold text-[#1b4332] shadow-lg transition-all duration-200 hover:bg-[#26a99c] active:scale-95" data-testid="banner-booking-button">
                Jetzt Reparatur anfragen <ArrowRight className="h-5 w-5" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white py-8" data-testid="landing-footer">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 text-sm text-slate-500 sm:px-6">
          <p>© 2026 recova GmbH – Willy-Messerschmitt-Str. 14, 50126 Bergheim</p>
          <div className="flex gap-6">
            <a href="tel:022715059265" className="hover:text-[#1b4332]" data-testid="footer-phone">02271 - 5059265</a>
            <a href="mailto:info@recova-service.de" className="hover:text-[#1b4332]" data-testid="footer-email">info@recova-service.de</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
